import { Button, Textarea } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Building2,
  Calendar,
  Clock,
  MessageSquare,
  Loader2,
  User,
  Bot,
} from 'lucide-react';
import { adminApi, Profile, Conversation } from '../lib/admin-api';

type ContactWithMetadata = Profile & { metadata?: Record<string, unknown> };

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<ContactWithMetadata | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!id) return;
      setLoading(true);

      const [contactData, contactConversations] = await Promise.all([
        adminApi.getContact(id),
        adminApi.getContactConversations(id),
      ]);

      setContact(contactData);
      setConversations(contactConversations);

      // Load notes from metadata
      const contactNotes = (contactData?.metadata as Record<string, unknown>)?.agent_notes as string || '';
      setNotes(contactNotes);
      setSavedNotes(contactNotes);

      setLoading(false);
    }

    fetchData();
  }, [id]);

  const handleSaveNotes = async () => {
    if (!id || !contact) return;
    setSavingNotes(true);
    const currentMeta = (contact.metadata || {}) as Record<string, unknown>;
    const success = await adminApi.updateUser(id, {
      metadata: { ...currentMeta, agent_notes: notes },
    } as Partial<Profile>);
    if (success) {
      setSavedNotes(notes);
    }
    setSavingNotes(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-success';
      case 'away': return 'bg-warning';
      case 'busy': return 'bg-destructive';
      default: return 'bg-muted-foreground';
    }
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      customer: 'bg-muted text-foreground',
      employee: 'bg-info text-info-foreground',
      agent: 'bg-primary/15 text-link',
      supervisor: 'bg-info text-info-foreground',
      admin: 'bg-warning text-warning-foreground',
      owner: 'bg-destructive/10 text-error-text',
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${styles[role] || styles.customer}`}>
        {role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-link" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Contact not found</p>
        <Button variant="ghost"
          onClick={() => navigate('/contacts')}
          className="mt-4 text-link hover:text-link"
        >
          Back to contacts
        </Button>
      </div>
    );
  }

  return (
    <div className="support-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button aria-label="Go back" size="iconSm" variant="ghost"
          onClick={() => navigate('/contacts')}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="relative">
            <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center">
              <span className="text-xl font-semibold text-link">
                {(contact.display_name || contact.email || '?')[0].toUpperCase()}
              </span>
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-border ${getStatusColor(contact.status)}`} />
          </div>
          <div>
            <h1 className="support-page-title">
              {contact.display_name || 'Unknown Contact'}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              {getRoleBadge(contact.role)}
              <span className="text-sm text-muted-foreground capitalize">{contact.status}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversation History */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card rounded-lg border border-border">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">
                Conversations ({conversations.length})
              </h2>
            </div>

            {conversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                No conversations yet
              </div>
            ) : (
              <div className="divide-y divide-border">
                {conversations.map((conv) => (
                  <Button  variant="ghost"
                    key={conv.id}
                    onClick={() => navigate(`/conversations/${conv.id}`)}
                    className="h-auto whitespace-normal justify-start w-full text-left px-6 py-4 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {conv.title || `#${conv.id.slice(0, 8)}`}
                        </span>
                        {conv.ai_handled && (
                          <Bot className="w-4 h-4 text-info-foreground" />
                        )}
                      </div>
                      <StatusBadge status={conv.status} />
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="capitalize">{conv.channel}</span>
                      <span>{new Date(conv.created_at).toLocaleDateString()}</span>
                      {conv.agent?.display_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {conv.agent.display_name}
                        </span>
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact Info Sidebar */}
        <div className="space-y-4">
          {/* Details */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4">Details</h3>
            <dl className="space-y-3 text-sm">
              {contact.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <dt className="text-muted-foreground text-xs">Email</dt>
                    <dd className="text-foreground">{contact.email}</dd>
                  </div>
                </div>
              )}
              {contact.department && (
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <dt className="text-muted-foreground text-xs">Department</dt>
                    <dd className="text-foreground">{contact.department}</dd>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <dt className="text-muted-foreground text-xs">First Seen</dt>
                  <dd className="text-foreground">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <dt className="text-muted-foreground text-xs">Last Seen</dt>
                  <dd className="text-foreground">
                    {contact.last_seen_at
                      ? new Date(contact.last_seen_at).toLocaleDateString()
                      : 'Never'}
                  </dd>
                </div>
              </div>
            </dl>
          </div>

          {/* Agent Notes */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-3">Notes</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this contact..."
              className="w-full border border-border rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-ring focus:border-primary min-h-[100px]"
            />
            {notes !== savedNotes && (
              <Button variant="ghost"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="mt-2 px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary disabled:bg-primary transition-colors"
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    open: 'bg-success text-success-foreground',
    pending: 'bg-warning text-warning-foreground',
    resolved: 'bg-info text-info-foreground',
    closed: 'bg-muted text-foreground',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.open}`}>
      {status}
    </span>
  );
}
